export const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "UTC",
] as const;

export const FREQUENCIES = [
  { value: "1x_week", label: "1× per week", days: 1 },
  { value: "2x_week", label: "2× per week", days: 2 },
  { value: "3x_week", label: "3× per week", days: 3 },
  { value: "daily", label: "Every day", days: 7 },
] as const;

export type FrequencyValue = (typeof FREQUENCIES)[number]["value"];

export const maxDaysFor = (f: string) =>
  FREQUENCIES.find((x) => x.value === f)?.days ?? 1;

export const DAYS = [
  { value: 0, short: "Sun", long: "Sunday" },
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
];

export const LENGTHS = [
  { value: 800, label: "Short — 800 words" },
  { value: 1500, label: "Medium — 1500 words" },
  { value: 2500, label: "Long — 2500 words" },
];

/** Offset (ms) of a given instant in a given IANA timezone. */
function tzOffsetMs(date: Date, timeZone: string) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(date)) if (part.type !== "literal") p[part.type] = part.value;
  const asUTC = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour) === 24 ? 0 : Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return asUTC - date.getTime();
}

/** Converts a wall-clock date/time in a timezone into the correct UTC instant (DST safe). */
export function zonedToUtc(dateStr: string, timeStr: string, timeZone: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  let ts = naive - tzOffsetMs(new Date(naive), timeZone);
  // second pass handles DST boundaries
  ts = naive - tzOffsetMs(new Date(ts), timeZone);
  return new Date(ts);
}

/** Splits a UTC instant into date (yyyy-mm-dd) and time (HH:mm) strings in a timezone. */
export function utcToZonedParts(iso: string, timeZone: string): { date: string; time: string } {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(new Date(iso))) if (part.type !== "literal") p[part.type] = part.value;
  const hour = p.hour === "24" ? "00" : p.hour;
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${hour}:${p.minute}` };
}

/** Formats a UTC instant in the schedule's timezone. */
export function formatInTz(iso: string | Date, timeZone: string, opts?: Intl.DateTimeFormatOptions) {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...opts,
  }).format(date);
}

export function nextRunLabel(iso: string | null, timeZone: string) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

export const STATUS_META: Record<string, { label: string; icon: string; className: string }> = {
  scheduled: { label: "Scheduled", icon: "🕐", className: "bg-muted text-muted-foreground" },
  processing: { label: "Processing", icon: "⚙️", className: "bg-secondary/15 text-secondary" },
  published: { label: "Published", icon: "✅", className: "bg-accent text-accent-foreground" },
  draft: { label: "Draft", icon: "📝", className: "bg-muted text-muted-foreground" },
  failed: { label: "Failed", icon: "❌", className: "bg-destructive/10 text-destructive" },
  cancelled: { label: "Cancelled", icon: "🚫", className: "bg-muted text-muted-foreground" },
};
